import { Controller } from "@hotwired/stimulus"
import tippy from 'tippy.js'
import { cleanupTinyMCEUI } from '../lib/tinymce-editor.js'

// Connects to data-controller="turbo-modal"
export default class extends Controller {
  static targets = ["content"]
  static values = {
    undismissable: Boolean,
    outsideClickClose: Boolean
  }

  connect() {
    this.isExpanded = false
    this.isSidepanel = this.contentTarget.classList.contains('manage_modal--sidepanel')
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleMouseDown = this.handleMouseDown.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleFrameRender = this.handleFrameRender.bind(this)
    
    document.addEventListener('keydown', this.handleKeydown)
    this.element.addEventListener('mousedown', this.handleMouseDown)
    this.element.addEventListener('click', this.handleOutsideClick)
    
    // Listen for turbo frame updates to re-initialize tippy
    this.element.addEventListener('turbo:frame-render', this.handleFrameRender)

    // Only trigger slide-in animation for sidepanel modals
    if (this.isSidepanel) {
      requestAnimationFrame(() => {
        this.element.classList.add('is-visible')
      })
    } else {
      // Save scroll position before locking to prevent jump to top
      this.scrollPosition = window.scrollY
      // For full-screen modals, lock body scroll.
      document.body.style.top = `-${this.scrollPosition}px`
      document.body.classList.add('scroll-lock')
    }

    this.initializeTippy()
  }
  
  initializeTippy() {
    // Initialize tippy tooltips for modal content
    if (window.initializeTippy) {
      window.initializeTippy(this.element);
    } else {
      // Fallback to direct initialization if shared function isn't available
      tippy('[data-tippy-content]', {
        appendTo: () => this.element,
        allowHTML: true
      });
    }
  }
  
  handleFrameRender() {
    // Re-initialize tippy when frame content is updated (e.g., prev/next navigation)
    this.initializeTippy()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.element.removeEventListener('mousedown', this.handleMouseDown)
    this.element.removeEventListener('click', this.handleOutsideClick)
    this.element.removeEventListener('turbo:frame-render', this.handleFrameRender)

    if (!this.isSidepanel) {
      // Restore scroll position when unlocking
      document.body.classList.remove('scroll-lock')
      document.body.style.top = ''
      window.scrollTo(0, this.scrollPosition || 0)
    }
  }

  handleMouseDown(event) {
    // Only track mouse position if modal is dismissable
    if (!this.undismissableValue) {
      this.mouseDownInsideModal = this.contentTarget.contains(event.target)
    }
  }

  handleOutsideClick(event) {
    // Only close if not undismissable and outsideClickCloseValue is true and both mousedown and click were outside the modal
    if (!this.undismissableValue &&
        this.outsideClickCloseValue &&
        !this.mouseDownInsideModal &&
        !this.contentTarget.contains(event.target) &&
        event.target === this.element) {
      this.hideModal()
    }
  }

  handleKeydown(event) {
    // Only handle Escape if not undismissable
    if (event.key === "Escape" && !this.undismissableValue) {
      this.hideModal()
    }
  }

  cleanup() {
    const frame = document.getElementById('sidepanel')
    if (frame) {
      frame.innerHTML = ''
      frame.removeAttribute("src")
    }
  }

  hideModal() {
    cleanupTinyMCEUI();
    if (this.isSidepanel) {
      // Add animation before removing
      this.element.classList.remove('is-visible')

      // Wait for animation to complete before cleaning up
      this.element.addEventListener('transitionend', () => {
        this.cleanup()
        if (this.element.parentElement) {
          this.element.parentElement.removeAttribute("src")
          this.element.remove()
        }
      }, { once: true })
      return
    }

    if (this.contentTarget.classList.contains('manage_modal--lightbox')) {
      // For lightbox modals, check if opened via full page navigation (URL is /photos/...)
      // If so, go back. Otherwise just remove the modal overlay.
      if (window.location.pathname.startsWith('/photos/')) {
        window.history.back()
      } else {
        this.cleanup()
        if (this.element.parentElement) {
          this.element.parentElement.removeAttribute("src")
          this.element.remove()
        }
      }
    } else {
      // For other modals, remove immediately
      this.cleanup()
      if (this.element.parentElement) {
        this.element.parentElement.removeAttribute("src")
        this.element.remove()
      }
    }
  }

  expandModal(event) {
    event.preventDefault()
    this.isExpanded = !this.isExpanded

    if (this.isExpanded) {
      this.contentTarget.classList.add('manage_modal--expanded')
      event.currentTarget.innerHTML = "<i class='far fa-arrow-down-left-and-arrow-up-right-to-center'></i>"
    } else {
      this.contentTarget.classList.remove('manage_modal--expanded')
      event.currentTarget.innerHTML = "<i class='far fa-arrow-up-right-and-arrow-down-left-from-center'></i>"
    }
  }

  submit(event) {
    const form = event.currentTarget.closest('form');

    if (form) {
      form.submit();
    }
  }

  downloadFileAndClose(event) {
    event.preventDefault()
    const form = event.currentTarget.closest('form')
    if (!form) return
  
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    const url = `${form.action}?${params.toString()}`
    
    // Create a temporary link and click it
    const link = document.createElement('a')
    link.href = url
    link.dataset.turbo = 'false'
    document.body.appendChild(link)
    link.click()
    link.remove()
    
    this.hideModal()
  }
}
